import { supabase } from '../utils/supabaseClient';

// Aligns with schema: user_dashboard_settings has JSONB fields like
// notification_preferences, dashboard_config, widget_preferences, etc.
const UserDashboardSettingsService = {
  // userId MUST be users.id (UserContext.user.id), NOT the Supabase auth UID.
  // user_dashboard_settings.user_id is FK'd to users(id), and users.id is a
  // DISJOINT id space from auth.users.id — they are linked by users.auth_user_id.
  // Writing session.user.id here 409'd on the FK on every save, so the dashboard
  // preference never persisted. RLS on this table scopes on
  // company_id = user_company_id(), NOT on auth.uid(), so nothing here needs the
  // auth UID (verified against pg_policy live 2026-08-23).
  async get(companyId, userId) {
    if (!userId) return null;

    try {
      // ✅ FIXED: Use .maybeSingle() instead of .single() to handle empty result gracefully
      // .maybeSingle() returns null if no rows found, instead of throwing PGRST116 error
      const { data, error } = await supabase
        .from('user_dashboard_settings')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        console.warn('Failed to get dashboard settings:', error.message);
        return null;
      }

      if (!data) {
        return null;
      }

      return data;
    } catch (e) {
      console.warn('Exception getting dashboard settings:', e.message);
      return null;
    }
  },

  // userId MUST be users.id — see the note on get() above.
  async upsert(companyId, userId, patch) {
    if (!userId) return false;

    const bodyPrefs = (patch.notification_prefs || patch.notification_preferences || patch) || {};

    try {
      // First try to update existing record. `.select()` is required: a PostgREST
      // UPDATE that matches ZERO rows returns 204 with error === null, so testing
      // the error alone reports success and the INSERT below becomes unreachable —
      // the preference then silently reverts on reload for any user with no row.
      const { data: updated, error: updateError } = await supabase
        .from('user_dashboard_settings')
        .update({
          notification_preferences: bodyPrefs,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId)
        .select('user_id');

      // Only a row that actually matched counts as an update.
      if (!updateError && updated && updated.length > 0) {
        return true;
      }

      // `updateError` is NULL on the zero-row path - which is the exact case this
      // function exists to handle - so this MUST be null-guarded. Without the
      // guard it threw `TypeError: Cannot read properties of null (reading
      // 'status')` on every zero-row update and the INSERT below never ran,
      // which is worse than the original bug it was meant to fix.
      if (updateError && updateError.status === 406) {
        return true; // table doesn't exist yet - don't fail, just skip
      }

      // If update failed (no row), try insert
      const { error: insertError } = await supabase
        .from('user_dashboard_settings')
        .insert({
          user_id: userId,
          company_id: companyId,
          notification_preferences: bodyPrefs,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });

      if (!insertError) {
        return true;
      }

      if (insertError && insertError.status === 406) {
        return true; // table doesn't exist yet - don't fail, just skip
      }

      console.warn('Failed to upsert dashboard settings:', insertError?.message);
      return false;
    } catch (e) {
      console.error('Error in upsert:', e);
      return false;
    }
  }
};

export default UserDashboardSettingsService;
